import { UseQueryResult } from "@tanstack/react-query";
import {
  CalendarFill18Icon,
  CalendarOutline18Icon,
  ServiceFill18Icon,
  ServiceOutline18Icon,
  WarningFill18Icon,
  WarningOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { GlobalEventModal } from "features/CenterContent/RoleContent/GlobalEvents/GlobalEventModal";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { WithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { GlobalEvents } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/GlobalEvents";
import { ProductionDeviationsList } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/ProductionDeviationsList";
import { UpcomingEvents } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/UpcomingEvents";
import { Tabs, TabsSides } from "features/CenterContent/shared/Tabs/TabsSides";
import { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  DialogRoot,
  DropsDialogContent,
  DropsDialogHeader,
  DialogBody as SporDialogBody,
} from "shared/components/Spor";
import { ObtrusiveScrollCss } from "shared/components/transitions/scroll";
import { useDropsRole } from "stores/useDropsRole";
import styled from "styled-components";

const DialogBody = styled(SporDialogBody)`
  ${ObtrusiveScrollCss};
  overflow-y: auto;
  display: grid;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

export const productionDeviationTabId = "productionDeviations";

type UpcomingEventsModalProps = {
  selectedTabIds: string[];
  setSelectedTabIds: Dispatch<SetStateAction<string[]>>;
  onClose: () => void;
  upcomingInfrastructureEvents?: UseQueryResult<
    WithInternalMessages<InfrastructureResponse>[]
  >;
  globalEvents?: UseQueryResult<WithInternalMessages<GlobalEventResponse>[]>;
  productionDeviations?: UseQueryResult<string[]>;
};

export const EventsOverviewModal: FC<UpcomingEventsModalProps> = ({
  selectedTabIds,
  setSelectedTabIds,
  onClose,
  upcomingInfrastructureEvents,
  globalEvents,
  productionDeviations,
}) => {
  const { isVaktleder } = useDropsRole();
  const { t } = useTranslation();

  const header = isVaktleder()
    ? t("Oversikt hendelser")
    : t("Fremtidige hendelser");

  const numGlobalEvents = globalEvents?.data?.length ?? 0;
  const numUpcomingEvents = upcomingInfrastructureEvents?.data?.length ?? 0;
  const numProductionDeviation = productionDeviations?.data?.length ?? 0;

  const tabs: Tabs[] = [
    globalEvents && {
      id: "globalEvents",
      iconInactive: <WarningOutline18Icon />,
      iconActive: <WarningFill18Icon />,
      text: t("Globale"),
    },
    upcomingInfrastructureEvents && {
      id: "upcomingEvents",
      iconInactive: <CalendarOutline18Icon />,
      iconActive: <CalendarFill18Icon />,
      text: t("Fremtidige"),
    },
    productionDeviations && {
      id: productionDeviationTabId,
      iconInactive: <ServiceOutline18Icon />,
      iconActive: <ServiceFill18Icon color="icon.service" />,
      text: t("Produksjonsavvik"),
    },
  ].filter(Boolean) as Tabs[];

  const notifications = useMemo(
    () => ({
      globalEvents: numGlobalEvents,
      upcomingInfrastructureEvents: numUpcomingEvents,
      productionDeviations: numProductionDeviation,
    }),
    [numGlobalEvents, numUpcomingEvents, numProductionDeviation],
  );

  const [globalEventToEdit, setGlobalEventToEdit] =
    useState<WithInternalMessages<GlobalEventResponse> | null>(null);

  const isPending =
    (selectedTabIds.includes("upcomingEvents") &&
      upcomingInfrastructureEvents?.isPending) ||
    (selectedTabIds.includes("globalEvents") && globalEvents?.isPending) ||
    (selectedTabIds.includes("productionDeviations") &&
      productionDeviations?.isPending);

  const isFailure =
    (selectedTabIds.includes("globalEvents") && globalEvents?.isError) ||
    (selectedTabIds.includes("upcomingEvents") &&
      upcomingInfrastructureEvents?.isError) ||
    (selectedTabIds.includes("productionDeviations") &&
      productionDeviations?.isError);

  const showTabs = isVaktleder();

  if (globalEventToEdit) {
    return <GlobalEventModal onClose={onClose} event={globalEventToEdit} />;
  }

  return (
    <DialogRoot open onOpenChange={onClose} size="lg" placement="center">
      <DropsDialogContent maxH="80vh" minH="80vh" minW="700px">
        <DropsDialogHeader>{header}</DropsDialogHeader>
        {showTabs && (
          <TabsSides
            tabs={tabs}
            selectedTabIds={selectedTabIds}
            setSelectedTabIds={setSelectedTabIds}
            justifyContent="start"
            notifications={notifications}
            alwaysShowNotifications
            ml="18px"
            inModal
          />
        )}
        <DialogBody p="24px">
          {isPending && <SkeletonLoader skeletonType="internalEvents" />}
          {isFailure && <FailureMessage />}

          {globalEvents?.isSuccess &&
            selectedTabIds.includes("globalEvents") && (
              <GlobalEvents
                data={globalEvents.data}
                onEditClick={(event) => {
                  setGlobalEventToEdit(event);
                }}
              />
            )}

          {upcomingInfrastructureEvents?.isSuccess &&
            selectedTabIds.includes("upcomingEvents") && (
              <UpcomingEvents data={upcomingInfrastructureEvents.data} />
            )}
          {productionDeviations?.isSuccess &&
            selectedTabIds.includes("productionDeviations") && (
              <ProductionDeviationsList />
            )}
        </DialogBody>
      </DropsDialogContent>
    </DialogRoot>
  );
};
